import { formatKg } from '@/lib/format';
import type { KgString } from '@/lib/kg';
import { cn } from '@/lib/utils';

interface KgValueProps {
  value: KgString | null | undefined;
  /**
   * Defaults to 1 decimal place — operations spec sets 100g (0.1kg) as
   * the smallest unit, so per-package weights and aggregates are shown to
   * 1 dp. Server still stores numeric(14,3) for historical compat.
   */
  fractionDigits?: number;
  /** Show the "kg" unit suffix. Default true. */
  showUnit?: boolean;
  /** Visual tone for diff display. */
  tone?: 'default' | 'success' | 'warning' | 'error' | 'critical' | 'muted';
  className?: string;
}

const toneClass: Record<NonNullable<KgValueProps['tone']>, string> = {
  default: '',
  success: 'efs-text-success',
  warning: 'efs-text-warning',
  error: 'efs-text-error',
  critical: 'efs-text-critical',
  muted: 'efs-text-muted',
};

/**
 * Render a kg decimal string consistently (3 decimals, thousands grouped,
 * monospace digits). Per 06-ui-api §4 we never round-trip via float.
 */
export const KgValue = ({
  value,
  fractionDigits = 1,
  showUnit = true,
  tone = 'default',
  className,
}: KgValueProps) => {
  const formatted = formatKg(value, { fractionDigits });
  return (
    <span className={cn('font-mono tabular-nums', toneClass[tone], className)}>
      {formatted}
      {showUnit && value != null && value !== '' ? <span className="ml-1 text-xs">kg</span> : null}
    </span>
  );
};
